/**
 * Compact relative time for board history — "2h ago", "6d ago", "3w ago".
 *
 * The board shows one of these per row and up to a dozen in an expanded
 * trace, so the full "24 Aug 2026 at 3:46 PM GMT+5:30" form is unreadable
 * at that density: the teacher is scanning for recency, not booking a time.
 */
export function formatRelativeShort(
  date: Date,
  now: Date = new Date()
): string {
  const seconds = Math.max(0, (now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";

  // `min`, not `m`: at a glance "1m ago" reads as a month beside "1mo ago".
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;

  // Switched on days, not on months reaching 12: `days / 30` hits 12 at day
  // 360, which would render a year that has not passed as `0y ago`.
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** Whole days between `date` and now, floored. */
export function daysSince(date: Date, now: Date = new Date()): number {
  return Math.floor((now.getTime() - date.getTime()) / 86_400_000);
}
